'use client';

import { useState } from 'react';
import { MapPin, Clock, Calendar, ChevronRight, CalendarDays, Repeat } from 'lucide-react';
import { ResponsiveSheet, SheetPage } from '@church/nextjs-ui/components/ResponsiveSheet';
import type { RecurringSeries, UpcomingDate } from '@/types/serve-finder';
import { Img } from '@/lib/asset-url';
import { BaseCard } from './BaseCard';
import { CardHero } from './CardHero';
import { CardCTA } from './CardCTA';

interface SeriesCardProps {
  series: RecurringSeries;
}

export function SeriesCard({ series }: SeriesCardProps) {
  const [isOpen, setIsOpen] = useState(false);

  const nextDate = series.Upcoming_Dates[0];

  return (
    <>
      {/* Grid Card */}
      <BaseCard
        variant="standard"
        className="bg-card cursor-pointer text-left"
        onClick={() => setIsOpen(true)}
        header={
          <CardHero
            bgUrl={series.Image_URL}
            fgAlt={series.Title}
            dateStrip={nextDate ? <>Next: {nextDate.Date}</> : undefined}
          />
        }
        body={
          <div className="flex flex-col">
            <div className="p-4 pb-3">
              <h3 className="text-foreground text-base leading-tight font-extrabold">
                {series.Title}
              </h3>
              {series.Subtitle && (
                <p className="text-muted-foreground mt-0.5 text-xs font-semibold tracking-wider uppercase">
                  {series.Subtitle}
                </p>
              )}

              <div className="mt-2 space-y-1.5">
                <div className="text-muted-foreground flex items-center gap-1.5 text-sm">
                  <MapPin className="h-3.5 w-3.5 shrink-0" />
                  {series.Congregation_Name}
                </div>
                <div className="text-muted-foreground flex items-center gap-1.5 text-sm">
                  <Repeat className="h-3.5 w-3.5 shrink-0" />
                  {series.Schedule}
                </div>
              </div>
            </div>
          </div>
        }
        footer={<CardCTA label="Upcoming Dates" useDefaultColors />}
      />

      {/* Series Detail Sheet */}
      <ResponsiveSheet
        open={isOpen}
        onClose={() => setIsOpen(false)}
        maxWidth="max-w-3xl"
        noPanelPadding
        panelClassName="bg-card"
        header={
          <div className="relative">
            <div className="relative aspect-video w-full overflow-hidden">
              <Img
                src={series.Image_URL}
                alt={series.Title}
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
            </div>

            <div className="border-border bg-card border-b px-4 py-3">
              <h2 className="text-foreground text-lg leading-tight font-extrabold sm:text-xl">
                {series.Title}
                {series.Subtitle && (
                  <span className="text-muted-foreground ml-2 text-sm font-semibold">
                    {series.Subtitle}
                  </span>
                )}
              </h2>
              <div className="text-muted-foreground mt-1 flex flex-wrap items-center gap-3 text-xs">
                <span className="flex items-center gap-1">
                  <MapPin className="h-3.5 w-3.5" />
                  {series.Congregation_Name}
                </span>
                <span className="flex items-center gap-1">
                  <Repeat className="h-3.5 w-3.5" />
                  {series.Schedule}
                </span>
              </div>
            </div>
          </div>
        }
      >
        <SheetPage name="main">
          <div>
            <div className="space-y-5 p-4">
              <p className="text-muted-foreground text-sm leading-relaxed">{series.Description}</p>

              {/* Upcoming Dates */}
              {series.Upcoming_Dates.length > 0 && (
                <div>
                  <p className="text-muted-foreground mb-3 flex items-center gap-2 text-xs font-semibold tracking-wider uppercase">
                    <CalendarDays className="h-4 w-4 text-[var(--brand-secondary)]" />
                    Upcoming Dates
                  </p>

                  <div className="space-y-2">
                    {series.Upcoming_Dates.map((date) => (
                      <DateRow key={date.ID} date={date} />
                    ))}
                  </div>
                </div>
              )}
            </div>

            {series.Page_URL && (
              <a
                href={series.Page_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="border-border flex items-center justify-center gap-2 border-t py-4 text-sm font-semibold tracking-wide text-gray-900 uppercase transition-opacity hover:opacity-80"
              >
                Learn More
                <ChevronRight className="h-4 w-4" />
              </a>
            )}
          </div>
        </SheetPage>
      </ResponsiveSheet>
    </>
  );
}

// ---------------------------------------------------------------------------
// Date Row — individual upcoming date within the series
// ---------------------------------------------------------------------------

function DateRow({ date }: { date: UpcomingDate }) {
  const hasSpecialImage = date.Image_URL != null;
  const hasRegistration = date.Registration_URL != null;

  return (
    <div
      className={`border-border bg-background flex items-start gap-3 border p-3 ${
        hasSpecialImage ? 'flex-col sm:flex-row' : ''
      }`}
    >
      {/* Special image for unique dates (e.g. retreat) */}
      {hasSpecialImage && (
        <div className="w-full overflow-hidden sm:h-20 sm:w-32 sm:shrink-0">
          <Img
            src={date.Image_URL!}
            alt={date.Topic ?? ''}
            className="h-full w-full object-cover"
          />
        </div>
      )}

      <div className="flex flex-1 items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-foreground flex items-center gap-2 text-sm font-bold">
            <CalendarDays className="text-muted-foreground h-3.5 w-3.5 shrink-0" />
            {date.Date}
          </div>
          <p className="text-muted-foreground mt-0.5 flex items-center gap-1.5 text-xs">
            <Clock className="h-3 w-3" />
            {date.Time}
          </p>
          {date.Series && (
            <p className="mt-1 text-xs font-medium text-[var(--brand-secondary)]">{date.Series}</p>
          )}
          {date.Topic && <p className="text-muted-foreground mt-0.5 text-xs">{date.Topic}</p>}
        </div>

        {hasRegistration && (
          <a
            href={date.Registration_URL!}
            target="_blank"
            rel="noopener noreferrer"
            className="flex shrink-0 items-center gap-1 text-xs font-semibold tracking-wide text-[var(--brand-secondary)] uppercase transition-opacity hover:opacity-80"
          >
            Register
            <ChevronRight className="h-3.5 w-3.5" />
          </a>
        )}
      </div>
    </div>
  );
}
